import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Router, Activity, Wifi, Users, AlertCircle, RefreshCw, Server, HardDrive, Cpu } from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../services/api';

export default function Mikrotik() {
  const [activeTab, setActiveTab] = useState('status');

  const { data: status, isLoading: statusLoading, refetch: refetchStatus } = useQuery({
    queryKey: ['mikrotik-status'],
    queryFn: async () => {
      const response = await api.get('/mikrotik/status');
      return response.data;
    },
    retry: false,
    refetchInterval: 30000, // Refrescar cada 30 segundos
  });

  const { data: interfaces, isLoading: interfacesLoading, refetch: refetchInterfaces } = useQuery({
    queryKey: ['mikrotik-interfaces'],
    queryFn: async () => {
      const response = await api.get('/mikrotik/interfaces');
      return response.data;
    },
    retry: false,
    enabled: activeTab === 'interfaces',
  });

  const { data: pppoeUsers, isLoading: pppoeLoading, refetch: refetchPppoe } = useQuery({
    queryKey: ['mikrotik-pppoe'],
    queryFn: async () => {
      const response = await api.get('/mikrotik/pppoe-users');
      return response.data;
    },
    retry: false,
    enabled: activeTab === 'pppoe',
  });

  const { data: hotspotUsers, isLoading: hotspotLoading, refetch: refetchHotspot } = useQuery({
    queryKey: ['mikrotik-hotspot'],
    queryFn: async () => {
      const response = await api.get('/mikrotik/hotspot-users');
      return response.data;
    },
    retry: false,
    enabled: activeTab === 'hotspot',
  });

  const handleRefresh = () => {
    if (activeTab === 'status') refetchStatus();
    if (activeTab === 'interfaces') refetchInterfaces();
    if (activeTab === 'pppoe') refetchPppoe();
    if (activeTab === 'hotspot') refetchHotspot();
    toast.success('Datos actualizados');
  };

  const isLoading = statusLoading || interfacesLoading || pppoeLoading || hotspotLoading;

  // Si MikroTik no está configurado
  if (status && !status.online && status.error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold text-gray-900">Monitoreo MikroTik</h1>
        </div>

        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-8 text-center">
          <AlertCircle size={48} className="mx-auto text-yellow-600 mb-4" />
          <h2 className="text-xl font-bold text-yellow-800 mb-2">MikroTik no configurado</h2>
          <p className="text-yellow-700 mb-4">
            Para monitorear tu router MikroTik, necesitas configurar la conexion en la seccion de Configuracion.
          </p>
          <div className="bg-white rounded-lg p-4 text-left max-w-lg mx-auto">
            <h3 className="font-bold text-gray-800 mb-2">Pasos:</h3>
            <ol className="list-decimal list-inside space-y-1 text-sm text-gray-600">
              <li>Ve a <strong>Configuracion → MikroTik</strong></li>
              <li>Habilita el monitoreo</li>
              <li>Ingresa la IP de tu router</li>
              <li>Ingresa usuario y contraseña</li>
              <li>Guarda los cambios</li>
            </ol>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Router size={28} className="text-primary-600" />
          <h1 className="text-3xl font-bold text-gray-900">Monitoreo MikroTik</h1>
        </div>
        <button
          onClick={handleRefresh}
          disabled={isLoading}
          className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50">
          <RefreshCw size={18} className={isLoading ? 'animate-spin' : ''} />
          <span>Actualizar</span>
        </button>
      </div>

      {/* Tabs */}
      <div className="flex space-x-2 bg-white p-2 rounded-xl shadow-sm border border-gray-100 overflow-x-auto">
        {[
          { id: 'status', label: 'Estado', icon: Activity },
          { id: 'interfaces', label: 'Interfaces', icon: Server },
          { id: 'pppoe', label: 'PPPoE', icon: Users },
          { id: 'hotspot', label: 'Hotspot', icon: Wifi },
        ].map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg whitespace-nowrap transition-colors ${
                activeTab === tab.id
                  ? 'bg-primary-100 text-primary-700 font-medium'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}>
              <Icon size={18} />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Contenido */}
      {activeTab === 'status' && status && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Estado Online */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-gray-500">Estado</h3>
              <div className={`w-3 h-3 rounded-full ${status.online ? 'bg-green-500' : 'bg-red-500'}`}></div>
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {status.online ? 'Online' : 'Offline'}
            </p>
            <p className="text-sm text-gray-500 mt-1">{status.nombre || 'Sin nombre'}</p>
          </div>

          {/* CPU */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-gray-500">CPU Load</h3>
              <Cpu size={20} className="text-blue-500" />
            </div>
            <p className="text-2xl font-bold text-gray-900">{status.cpuLoad || 0}%</p>
            <p className="text-sm text-gray-500 mt-1">{status.boardName || 'Unknown'}</p>
          </div>

          {/* Memoria */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-gray-500">Memoria Libre</h3>
              <HardDrive size={20} className="text-purple-500" />
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {status.freeMemory ? Math.round(status.freeMemory / 1024 / 1024) : 0} MB
            </p>
            <p className="text-sm text-gray-500 mt-1">
              Total: {status.totalMemory ? Math.round(status.totalMemory / 1024 / 1024) : 0} MB
            </p>
          </div>

          {/* Uptime */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-gray-500">Uptime</h3>
              <Activity size={20} className="text-green-500" />
            </div>
            <p className="text-2xl font-bold text-gray-900">{status.uptime || 'N/A'}</p>
            <p className="text-sm text-gray-500 mt-1">v{status.version || '?'}</p>
          </div>

          {/* Info adicional */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 md:col-span-2 lg:col-span-4">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Informacion del Sistema</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Arquitectura:</span>
                <p className="font-medium">{status.architecture || 'N/A'}</p>
              </div>
              <div>
                <span className="text-gray-500">Board:</span>
                <p className="font-medium">{status.boardName || 'N/A'}</p>
              </div>
              <div>
                <span className="text-gray-500">Version:</span>
                <p className="font-medium">{status.version || 'N/A'}</p>
              </div>
              <div>
                <span className="text-gray-500">Disco Libre:</span>
                <p className="font-medium">
                  {status.freeHdd ? Math.round(status.freeHdd / 1024 / 1024) : 0} MB
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'interfaces' && interfaces && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nombre</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">RX (Mbps)</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">TX (Mbps)</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Comentario</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {interfaces.map((iface) => (
                <tr key={iface.name} className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-medium text-gray-900">{iface.name}</td>
                  <td className="px-6 py-4 text-gray-600">{iface.type}</td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                      iface.running ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {iface.running ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right font-medium text-blue-600">
                    {iface.rxSpeed.toFixed(2)}
                  </td>
                  <td className="px-6 py-4 text-right font-medium text-green-600">
                    {iface.txSpeed.toFixed(2)}
                  </td>
                  <td className="px-6 py-4 text-gray-600">{iface.comment || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'pppoe' && pppoeUsers && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Usuario</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Servicio</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">IP</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Caller ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Uptime</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {pppoeUsers.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                    No hay usuarios PPPoE conectados
                  </td>
                </tr>
              ) : (
                pppoeUsers.map((user, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium text-gray-900">{user.name}</td>
                    <td className="px-6 py-4 text-gray-600">{user.service}</td>
                    <td className="px-6 py-4 text-gray-600 font-mono text-sm">{user.address}</td>
                    <td className="px-6 py-4 text-gray-600 font-mono text-sm">{user.callerId}</td>
                    <td className="px-6 py-4 text-gray-600">{user.uptime}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'hotspot' && hotspotUsers && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Usuario</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">IP</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">MAC</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Bytes In</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Bytes Out</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Uptime</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {hotspotUsers.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                    No hay usuarios Hotspot conectados
                  </td>
                </tr>
              ) : (
                hotspotUsers.map((user, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium text-gray-900">{user.user}</td>
                    <td className="px-6 py-4 text-gray-600 font-mono text-sm">{user.address}</td>
                    <td className="px-6 py-4 text-gray-600 font-mono text-sm">{user.macAddress}</td>
                    <td className="px-6 py-4 text-right text-gray-600">
                      {(Number(user.bytesIn) / 1024 / 1024).toFixed(2)} MB
                    </td>
                    <td className="px-6 py-4 text-right text-gray-600">
                      {(Number(user.bytesOut) / 1024 / 1024).toFixed(2)} MB
                    </td>
                    <td className="px-6 py-4 text-gray-600">{user.uptime}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
